/**
 * MapCanvas - Main canvas component for warehouse map visualization
 * Uses Konva.js for 2D canvas rendering
 * Supports navigation graph display and picking path visualization
 */
import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Stage, Layer, Rect, Text, Group, Transformer, Image as KonvaImage, Line, Circle } from 'react-konva';
import Konva from 'konva';
import { Box, IconButton, Tooltip, Typography, Paper } from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as CenterIcon,
  GridOn as GridIcon,
  GridOff as GridOffIcon,
  PanTool as PanIcon,
  NearMe as SelectIcon,
  Route as RouteIcon,
  AccountTree as NavigationIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { MapElement, Position, EditorMode, ELEMENT_TYPES } from './types';
import { NavigationGraph, NavigationNode, PickingPath } from './pathTypes';

type KonvaEventObject<T> = Konva.KonvaEventObject<T>;

/** Bounding box shape used by Konva Transformer boundBoxFunc */
interface TransformerBoundBox {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

interface MapCanvasProps {
  width: number;
  height: number;
  elements: MapElement[];
  selectedElementId: string | null;
  backgroundImage?: string;
  mode: EditorMode;
  showGrid: boolean;
  gridSize: number;
  snapToGrid: boolean;
  onElementSelect: (element: MapElement | null) => void;
  onElementMove: (id: string, position: Position) => void;
  onElementResize: (id: string, width: number, height: number) => void;
  onElementRotate: (id: string, rotation: number) => void;
  onModeChange: (mode: EditorMode) => void;
  onToggleGrid: () => void;
  // Navigation and path visualization props
  navigationGraph?: NavigationGraph;
  showNavigation?: boolean;
  onToggleNavigation?: () => void;
  pickingPath?: PickingPath;
  showPickingPath?: boolean;
  onTogglePickingPath?: () => void;
  highlightedStepIndex?: number;
  highlightedLocationIds?: number[];
  onRackClick?: (element: MapElement) => void;
  readOnly?: boolean;
  // Start point (office/entry)
  startPoint?: { x: number; y: number; name?: string } | null;
  onStartPointSet?: (position: Position) => void;
}

const MapCanvas: React.FC<MapCanvasProps> = ({
  width,
  height,
  elements,
  selectedElementId,
  backgroundImage,
  mode,
  showGrid,
  gridSize,
  snapToGrid,
  onElementSelect,
  onElementMove,
  onElementResize,
  onElementRotate,
  onModeChange,
  onToggleGrid,
  // Navigation and path props
  navigationGraph,
  showNavigation = false,
  onToggleNavigation,
  pickingPath,
  showPickingPath = false,
  onTogglePickingPath,
  highlightedStepIndex,
  highlightedLocationIds = [],
  onRackClick,
  readOnly = false,
  startPoint,
  onStartPointSet,
}) => {
  const { t } = useTranslation();
  const stageRef = useRef<any>(null);
  const transformerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const [zoom, setZoom] = useState(1);
  const [stagePos, setStagePos] = useState<Position>({ x: 0, y: 0 });
  const [containerSize, setContainerSize] = useState({ width: 800, height: 600 });
  const [bgImage, setBgImage] = useState<HTMLImageElement | null>(null);
  const [pathAnimationOffset, setPathAnimationOffset] = useState(0);
  const safeMapWidth = Math.max(1, Number.isFinite(width) ? width : 1);
  const safeMapHeight = Math.max(1, Number.isFinite(height) ? height : 1);
  const canRenderStage = containerSize.width > 10 && containerSize.height > 10 && safeMapWidth > 1 && safeMapHeight > 1;
  
  // Animate picking path - optimized with requestAnimationFrame for better performance
  useEffect(() => {
    if (!showPickingPath || !pickingPath) return;
    
    let animationFrameId: number;
    let lastTime = 0;
    
    const animate = (currentTime: number) => {
      if (currentTime - lastTime >= 100) { // Throttle to ~10fps instead of 10ms intervals
        setPathAnimationOffset(prev => (prev + 1) % 20);
        lastTime = currentTime;
      }
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animationFrameId = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [showPickingPath, pickingPath]);
  
  // Load background image
  useEffect(() => {
    if (backgroundImage) {
      const img = new window.Image();
      img.crossOrigin = 'anonymous'; // Handle CORS if needed
      img.onload = () => {
        // Only set if image has valid dimensions
        if (img.width > 0 && img.height > 0) {
          setBgImage(img);
        } else {
          console.warn('Background image has invalid dimensions');
          setBgImage(null);
        }
      };
      img.onerror = () => {
        console.error('Failed to load background image');
        setBgImage(null);
      };
      img.src = backgroundImage;
      
      // Cleanup: revoke object URL if it's a blob URL
      return () => {
        if (img.src && img.src.startsWith('blob:')) {
          URL.revokeObjectURL(img.src);
        }
        img.src = ''; // Clear image source to free memory
      };
    } else {
      setBgImage(null);
    }
  }, [backgroundImage]);
  
  // Update container size on resize (clamp to min 1 to avoid canvas 0x0 → drawImage InvalidStateError)
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const w = containerRef.current.offsetWidth;
        const h = containerRef.current.offsetHeight;
        setContainerSize({
          width: Math.max(1, w || 0),
          height: Math.max(1, h || 0),
        });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    const el = containerRef.current;
    let ro: ResizeObserver | null = null;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateSize);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('resize', updateSize);
      if (ro && el) {
        try {
          ro.unobserve(el);
        } catch (_) {}
      }
    };
  }, []);
  
  // Update transformer when selection changes
  useEffect(() => {
    if (transformerRef.current && stageRef.current) {
      const stage = stageRef.current;
      const selectedNode = stage.findOne(`#element-${selectedElementId}`);
      
      if (selectedNode) {
        transformerRef.current.nodes([selectedNode]);
      } else {
        transformerRef.current.nodes([]);
      }
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [selectedElementId]);
  
  // Handle zoom
  const handleWheel = useCallback((e: KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    
    const stage = stageRef.current;
    if (!stage) return;
    
    const oldScale = zoom;
    const pointer = stage.getPointerPosition();
    
    const scaleBy = 1.1;
    const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;
    const clampedScale = Math.max(0.1, Math.min(5, newScale));
    
    setZoom(clampedScale);
    
    if (pointer) {
      const mousePointTo = {
        x: (pointer.x - stagePos.x) / oldScale,
        y: (pointer.y - stagePos.y) / oldScale,
      };
      
      setStagePos({
        x: pointer.x - mousePointTo.x * clampedScale,
        y: pointer.y - mousePointTo.y * clampedScale,
      });
    }
  }, [zoom, stagePos]);
  
  // Handle stage click (deselect)
  const handleStageClick = (e: KonvaEventObject<MouseEvent>) => {
    // Handle set_start_point mode - click anywhere to place the start point
    if (mode === 'set_start_point' && onStartPointSet) {
      const stage = stageRef.current;
      if (stage) {
        const pointerPos = stage.getPointerPosition();
        if (pointerPos) {
          // Convert screen coordinates to map coordinates (accounting for zoom/pan)
          const mapX = (pointerPos.x - stagePos.x) / zoom;
          const mapY = (pointerPos.y - stagePos.y) / zoom;
          onStartPointSet({ x: mapX, y: mapY });
          onModeChange('select'); // Switch back to select mode after placing
        }
      }
      return;
    }
    
    if (e.target === e.target.getStage()) {
      onElementSelect(null);
    }
  };
  
  // Handle element drag
  const handleDragEnd = (e: KonvaEventObject<DragEvent>, element: MapElement) => {
    let newX = e.target.x();
    let newY = e.target.y();
    
    if (snapToGrid) {
      newX = Math.round(newX / gridSize) * gridSize;
      newY = Math.round(newY / gridSize) * gridSize;
    }
    
    onElementMove(element.id, { x: newX, y: newY });
  };
  
  // Track base dimensions when transform starts
  const transformStartDimensions = useRef<{ width: number; height: number } | null>(null);
  
  // Handle transform start - store initial dimensions
  const handleTransformStart = (e: KonvaEventObject<Event>, element: MapElement) => {
    transformStartDimensions.current = {
      width: element.dimensions.width,
      height: element.dimensions.height,
    };
  };
  
  // Handle transform in real-time (resize/rotate) - updates UI as user drags
  const handleTransform = (e: KonvaEventObject<Event>, element: MapElement) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    
    // Use stored base dimensions to prevent compounding
    const baseDimensions = transformStartDimensions.current || element.dimensions;
    
    // Validate base dimensions
    if (!baseDimensions || baseDimensions.width <= 0 || baseDimensions.height <= 0) {
      return; // Skip if invalid
    }
    
    // Calculate new dimensions based on scale
    const newWidth = Math.max(20, baseDimensions.width * scaleX);
    const newHeight = Math.max(20, baseDimensions.height * scaleY);
    
    // Validate calculated dimensions
    if (newWidth <= 0 || newHeight <= 0 || !isFinite(newWidth) || !isFinite(newHeight)) {
      return; // Skip if invalid
    }
    
    // Update dimensions, rotation, and position in real-time
    onElementResize(element.id, newWidth, newHeight);
    onElementRotate(element.id, node.rotation());
    onElementMove(element.id, { x: node.x(), y: node.y() });
  };
  
  // Handle transform end (final commit + cleanup)
  const handleTransformEnd = (e: KonvaEventObject<Event>, element: MapElement) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    
    // Use stored base dimensions for final calculation
    const baseDimensions = transformStartDimensions.current || element.dimensions;
    
    // Validate base dimensions
    if (!baseDimensions || baseDimensions.width <= 0 || baseDimensions.height <= 0) {
      // Reset scale and clear stored dimensions
      node.scaleX(1);
      node.scaleY(1);
      transformStartDimensions.current = null;
      return;
    }
    
    // Final dimensions
    const finalWidth = Math.max(20, baseDimensions.width * scaleX);
    const finalHeight = Math.max(20, baseDimensions.height * scaleY);
    
    // Validate calculated dimensions
    if (finalWidth <= 0 || finalHeight <= 0 || !isFinite(finalWidth) || !isFinite(finalHeight)) {
      // Reset scale and clear stored dimensions
      node.scaleX(1);
      node.scaleY(1);
      transformStartDimensions.current = null;
      return;
    }
    
    // Commit final values
    onElementResize(element.id, finalWidth, finalHeight);
    onElementRotate(element.id, node.rotation());
    onElementMove(element.id, { x: node.x(), y: node.y() });
    
    // Reset scale to 1 (dimensions are now in the element props)
    node.scaleX(1);
    node.scaleY(1);
    
    // Clear stored dimensions
    transformStartDimensions.current = null;
  };
  
  // Get color based on element type and status
  const getElementColor = (element: MapElement): string => {
    // Check if element is highlighted for picking
    const elementIdNum = parseInt(element.id, 10);
    if (!isNaN(elementIdNum) && highlightedLocationIds.includes(elementIdNum)) {
      return '#ffeb3b'; // Yellow for highlighted picking locations
    }
    if (element.id === selectedElementId) {
      return '#90caf9'; // Selected blue
    }
    if (element.status === 'FULL') {
      return '#ffcdd2'; // Red for full
    }
    if (element.status === 'BLOCKED' || element.status === 'MAINTENANCE') {
      return '#e0e0e0'; // Gray for blocked
    }
    return element.color || '#e3f2fd';
  };
  
  // Render navigation graph layer
  const renderNavigationLayer = () => {
    if (!showNavigation || !navigationGraph) return null;
    
    const nodeMap = new Map<number, NavigationNode>();
    navigationGraph.nodes.forEach(node => nodeMap.set(node.id, node));
    
    return (
      <>
        {/* Edges */}
        {navigationGraph.edges.map(edge => {
          const fromNode = nodeMap.get(edge.fromNodeId);
          const toNode = nodeMap.get(edge.toNodeId);
          if (!fromNode || !toNode) return null;
          
          return (
            <Line
              key={`edge-${edge.id}`}
              points={[
                fromNode.position.x,
                fromNode.position.y,
                toNode.position.x,
                toNode.position.y,
              ]}
              stroke="#9e9e9e"
              strokeWidth={2}
              dash={edge.isBidirectional ? undefined : [5, 5]}
              opacity={0.6}
            />
          );
        })}
        
        {/* Nodes */}
        {navigationGraph.nodes.map(node => {
          const color = node.nodeType === 'ENTRY' ? '#4caf50' :
                       node.nodeType === 'RACK_ACCESS' ? '#2196f3' :
                       '#ff9800';
          const radius = node.nodeType === 'ENTRY' ? 10 : 6;
          
          return (
            <Group key={`node-${node.id}`}>
              <Circle
                x={node.position.x}
                y={node.position.y}
                radius={radius}
                fill={color}
                stroke="#fff"
                strokeWidth={2}
                shadowColor="black"
                shadowBlur={4}
                shadowOpacity={0.3}
              />
              {node.nodeType === 'ENTRY' && (
                <Text
                  x={node.position.x + 12}
                  y={node.position.y - 6}
                  text={node.code}
                  fontSize={10}
                  fill="#333"
                  fontStyle="bold"
                />
              )}
            </Group>
          );
        })}
      </>
    );
  };
  
  // Render picking path layer
  const renderPickingPathLayer = () => {
    if (!showPickingPath || !pickingPath) return null;

    const hasPath = pickingPath.pathCoordinates.length >= 2;

    // Build node lookup from navigation graph
    const navNodeMap = new Map<number, { x: number; y: number }>();
    if (navigationGraph) {
      navigationGraph.nodes.forEach(n => navNodeMap.set(n.id, n.position));
    }

    // Main route points (A* graph path)
    const pathPoints: number[] = [];
    if (hasPath) {
      pickingPath.pathCoordinates.forEach(c => pathPoints.push(c.x, c.y));
    }

    // Direction arrow helper: draw a triangle at a point along the path
    const renderArrows = () => {
      if (pathPoints.length < 4) return null;
      const arrows: React.ReactNode[] = [];
      let accumulated = 0;
      const arrowEvery = 60; // pixels between arrows
      let nextArrowAt = arrowEvery;
      for (let i = 0; i < pathPoints.length - 2; i += 2) {
        const ax = pathPoints[i], ay = pathPoints[i + 1];
        const bx = pathPoints[i + 2], by = pathPoints[i + 3];
        const dx = bx - ax, dy = by - ay;
        const segLen = Math.sqrt(dx * dx + dy * dy);
        if (segLen < 1) continue;
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);
        while (nextArrowAt <= accumulated + segLen) {
          const t = (nextArrowAt - accumulated) / segLen;
          const px = ax + dx * t, py = ay + dy * t;
          arrows.push(
            <Line
              key={`arrow-${arrows.length}`}
              points={[-5, -4, 5, 0, -5, 4]}
              x={px}
              y={py}
              rotation={angle}
              fill="#ff9800"
              closed
              opacity={0.9}
            />
          );
          nextArrowAt += arrowEvery;
        }
        accumulated += segLen;
      }
      return arrows;
    };

    return (
      <>
        {/* White border under main path for contrast */}
        {hasPath && (
          <Line
            points={pathPoints}
            stroke="#ffffff"
            strokeWidth={8}
            lineCap="round"
            lineJoin="round"
            opacity={0.6}
          />
        )}

        {/* Main route line — solid orange */}
        {hasPath && (
          <Line
            points={pathPoints}
            stroke="#ff9800"
            strokeWidth={5}
            lineCap="round"
            lineJoin="round"
            opacity={0.85}
          />
        )}

        {/* Animated dashed overlay to show movement direction */}
        {hasPath && (
          <Line
            points={pathPoints}
            stroke="#ffffff"
            strokeWidth={2}
            lineCap="round"
            lineJoin="round"
            dash={[8, 12]}
            dashOffset={-pathAnimationOffset}
            opacity={0.9}
          />
        )}

        {/* Direction arrows along the path */}
        {renderArrows()}

        {/* Approach lines: from path (access node) to rack center for each step */}
        {pickingPath.steps.map((step) => {
          const rackPos = step.location.position;
          let accessPos: { x: number; y: number } | null = null;
          if (step.navigationNodeId && navNodeMap.has(step.navigationNodeId)) {
            accessPos = navNodeMap.get(step.navigationNodeId)!;
          }
          if (!accessPos) return null;
          const dx = rackPos.x - accessPos.x;
          const dy = rackPos.y - accessPos.y;
          if (Math.sqrt(dx * dx + dy * dy) < 3) return null;
          return (
            <Group key={`approach-${step.order}`}>
              {/* Dashed approach line from aisle to rack */}
              <Line
                points={[accessPos.x, accessPos.y, rackPos.x, rackPos.y]}
                stroke="#ff5722"
                strokeWidth={2.5}
                dash={[4, 4]}
                lineCap="round"
                opacity={0.7}
              />
              {/* Small dot at access node */}
              <Circle
                x={accessPos.x}
                y={accessPos.y}
                radius={4}
                fill="#ff5722"
                opacity={0.8}
              />
            </Group>
          );
        })}

        {/* Step markers with numbers, quantity & product name on the racks */}
        {pickingPath.steps.map((step, index) => {
          const isHighlighted = highlightedStepIndex === index;
          const coord = step.location.position;
          const productShort = step.product.name.length > 18
            ? step.product.name.slice(0, 18) + '…'
            : step.product.name;
          const qtyLabel = `${step.quantityToPick}/${step.availableQuantity} szt`;

          return (
            <Group key={`step-marker-${step.order}`}>
              {isHighlighted && (
                <Circle
                  x={coord.x}
                  y={coord.y}
                  radius={25}
                  fill="#ffeb3b"
                  opacity={0.4}
                />
              )}
              <Circle
                x={coord.x}
                y={coord.y}
                radius={15}
                fill={isHighlighted ? '#f44336' : '#ff5722'}
                stroke="#fff"
                strokeWidth={2.5}
                shadowColor="rgba(0,0,0,0.4)"
                shadowBlur={6}
                shadowOffsetY={2}
              />
              <Text
                x={coord.x - (step.order >= 10 ? 8 : 5)}
                y={coord.y - 6}
                text={String(step.order)}
                fontSize={13}
                fill="#fff"
                fontStyle="bold"
              />
              {/* Info tooltip below marker */}
              <Rect
                x={coord.x - 50}
                y={coord.y + 18}
                width={100}
                height={28}
                fill="rgba(33,33,33,0.85)"
                cornerRadius={4}
              />
              <Text
                x={coord.x - 48}
                y={coord.y + 20}
                text={productShort}
                fontSize={8}
                fill="#fff"
                width={96}
                ellipsis
              />
              <Text
                x={coord.x - 48}
                y={coord.y + 31}
                text={qtyLabel}
                fontSize={8}
                fill="#ffcc80"
                fontStyle="bold"
                width={96}
              />
            </Group>
          );
        })}

        {/* START marker */}
        {pickingPath.pathCoordinates.length > 0 && (
          <Group>
            <Circle
              x={pickingPath.pathCoordinates[0].x}
              y={pickingPath.pathCoordinates[0].y}
              radius={10}
              fill="#4caf50"
              stroke="#fff"
              strokeWidth={2.5}
              shadowColor="rgba(0,0,0,0.3)"
              shadowBlur={4}
            />
            <Text
              x={pickingPath.pathCoordinates[0].x + 13}
              y={pickingPath.pathCoordinates[0].y - 7}
              text="START"
              fontSize={11}
              fill="#4caf50"
              fontStyle="bold"
            />
          </Group>
        )}

        {/* END marker */}
        {pickingPath.pathCoordinates.length >= 2 && (
          <Group>
            <Circle
              x={pickingPath.pathCoordinates[pickingPath.pathCoordinates.length - 1].x}
              y={pickingPath.pathCoordinates[pickingPath.pathCoordinates.length - 1].y}
              radius={8}
              fill="#f44336"
              stroke="#fff"
              strokeWidth={2}
            />
            <Text
              x={pickingPath.pathCoordinates[pickingPath.pathCoordinates.length - 1].x + 11}
              y={pickingPath.pathCoordinates[pickingPath.pathCoordinates.length - 1].y - 7}
              text="KONIEC"
              fontSize={10}
              fill="#f44336"
              fontStyle="bold"
            />
          </Group>
        )}
      </>
    );
  };
  
  // Render grid - optimized to use Line components instead of Rect for better performance
  const renderGrid = () => {
    if (!showGrid) return null;
    
    // Limit grid rendering to visible area + buffer to prevent memory issues
    const maxLines = 200; // Maximum number of grid lines to render
    const verticalLines = Math.min(Math.floor(safeMapWidth / gridSize) + 1, maxLines);
    const horizontalLines = Math.min(Math.floor(safeMapHeight / gridSize) + 1, maxLines);
    
    const lines = [];
    const strokeColor = '#e0e0e0';
    
    // Vertical lines - limit rendering
    for (let i = 0; i < verticalLines; i++) {
      const x = i * gridSize;
      if (x <= safeMapWidth) {
        lines.push(
          <Line
            key={`v-${x}`}
            points={[x, 0, x, safeMapHeight]}
            stroke={strokeColor}
            strokeWidth={1}
            opacity={0.5}
            listening={false}
          />
        );
      }
    }
    
    // Horizontal lines - limit rendering
    for (let i = 0; i < horizontalLines; i++) {
      const y = i * gridSize;
      if (y <= safeMapHeight) {
        lines.push(
          <Line
            key={`h-${y}`}
            points={[0, y, safeMapWidth, y]}
            stroke={strokeColor}
            strokeWidth={1}
            opacity={0.5}
            listening={false}
          />
        );
      }
    }
    
    return lines;
  };
  
  // Render element
  const renderElement = (element: MapElement) => {
    // Comprehensive validation - skip elements with invalid data
    if (!element || !element.id) {
      return null;
    }
    
    // Validate dimensions
    if (!element.dimensions || 
        typeof element.dimensions.width !== 'number' || 
        typeof element.dimensions.height !== 'number' ||
        !isFinite(element.dimensions.width) ||
        !isFinite(element.dimensions.height) ||
        element.dimensions.width <= 0 || 
        element.dimensions.height <= 0) {
      return null;
    }
    
    // Validate position
    if (!element.position ||
        typeof element.position.x !== 'number' ||
        typeof element.position.y !== 'number' ||
        !isFinite(element.position.x) ||
        !isFinite(element.position.y)) {
      return null;
    }
    
    // Ensure minimum dimensions
    const safeWidth = Math.max(20, element.dimensions.width);
    const safeHeight = Math.max(20, element.dimensions.height);
    const safeX = isFinite(element.position.x) ? element.position.x : 0;
    const safeY = isFinite(element.position.y) ? element.position.y : 0;
    const safeRotation = isFinite(element.rotation) ? element.rotation : 0;
    
    const isSelected = element.id === selectedElementId;
    const fillColor = getElementColor(element);
    const typeConfig = ELEMENT_TYPES.find(t => t.type === element.type);
    
    // Check if this element should be highlighted
    const elementIdNum = parseInt(element.id, 10);
    const isHighlightedLocation = !isNaN(elementIdNum) && highlightedLocationIds.includes(elementIdNum);
    const isRackType = ['RACK', 'COLD', 'HAZMAT'].includes(element.type);
    
    return (
      <Group
        key={element.id}
        id={`element-${element.id}`}
        x={safeX}
        y={safeY}
        width={safeWidth}
        height={safeHeight}
        rotation={safeRotation}
        draggable={mode === 'select' && !readOnly}
        onClick={() => {
          onElementSelect(element);
        }}
        onTap={() => {
          onElementSelect(element);
        }}
        onDblClick={() => {
          if (isRackType && onRackClick) {
            onRackClick(element);
          }
        }}
        onDblTap={() => {
          if (isRackType && onRackClick) {
            onRackClick(element);
          }
        }}
        onDragEnd={(e: KonvaEventObject<DragEvent>) => { if (!readOnly) handleDragEnd(e, element); }}
        onTransformStart={(e: KonvaEventObject<Event>) => { if (!readOnly) handleTransformStart(e, element); }}
        onTransform={(e: KonvaEventObject<Event>) => { if (!readOnly) handleTransform(e, element); }}
        onTransformEnd={(e: KonvaEventObject<Event>) => { if (!readOnly) handleTransformEnd(e, element); }}
      >
        {/* Highlight glow for picking locations */}
        {isHighlightedLocation && (
          <Rect
            width={safeWidth + 8}
            height={safeHeight + 8}
            x={-4}
            y={-4}
            fill="#ffeb3b"
            cornerRadius={8}
            opacity={0.5}
          />
        )}
        {/* Element rectangle - for transformer to resize */}
        <Rect
          name="elementRect"
          width={safeWidth}
          height={safeHeight}
          fill={fillColor}
          stroke={isSelected ? '#1976d2' : '#bdbdbd'}
          strokeWidth={isSelected ? 2 : 1}
          cornerRadius={4}
          shadowColor="black"
          shadowBlur={isSelected ? 8 : 2}
          shadowOpacity={isSelected ? 0.3 : 0.1}
          shadowOffsetY={2}
        />
        
        {/* Element label */}
        <Text
          text={`${typeConfig?.icon || ''} ${element.code || element.name || ''}`}
          fontSize={Math.min(12, Math.max(8, safeWidth / 8))}
          fontFamily="Roboto, sans-serif"
          fontStyle="bold"
          fill="#212121"
          width={Math.max(20, safeWidth - 8)}
          height={14}
          x={4}
          y={3}
          align="left"
          verticalAlign="top"
          ellipsis
          listening={false}
        />

        {/* Shelf mini cross-section for rack elements */}
        {isRackType && element.shelves && element.shelves.length > 0 ? (() => {
          const headerH = 16;
          const availH = safeHeight - headerH - 2;
          const shelfH = Math.max(10, Math.min(16, availH / element.shelves.length));
          const gap = Math.max(1, Math.min(2, (availH - shelfH * element.shelves.length) / Math.max(1, element.shelves.length)));
          const labelFontSize = Math.min(9, Math.max(6, safeWidth / 14));
          return (
            <Group x={2} y={headerH} listening={false}>
              {element.shelves.map((shelf, idx) => {
                const yOff = idx * (shelfH + gap);
                const fillOpacity = shelf.totalItems > 0 ? Math.min(0.6, 0.15 + shelf.totalItems * 0.03) : 0.05;
                const productLabel = shelf.topProducts.length > 0
                  ? shelf.topProducts.map(p => p.name.length > 10 ? p.name.slice(0, 10) + '..' : p.name).join(', ')
                  : '';
                return (
                  <Group key={shelf.level} y={yOff}>
                    <Rect
                      width={safeWidth - 4}
                      height={shelfH}
                      fill={shelf.totalItems > 0 ? '#1565c0' : '#e0e0e0'}
                      opacity={fillOpacity}
                      cornerRadius={2}
                    />
                    <Rect
                      width={safeWidth - 4}
                      height={shelfH}
                      stroke="#90a4ae"
                      strokeWidth={0.5}
                      cornerRadius={2}
                    />
                    <Text
                      text={`P${shelf.level}`}
                      fontSize={labelFontSize}
                      fontFamily="Roboto Mono, monospace"
                      fontStyle="bold"
                      fill="#37474f"
                      x={2}
                      y={1}
                      width={22}
                      height={shelfH - 2}
                      verticalAlign="middle"
                    />
                    <Text
                      text={shelf.totalItems > 0 ? `${shelf.totalItems}` : '-'}
                      fontSize={labelFontSize}
                      fontFamily="Roboto Mono, monospace"
                      fill={shelf.totalItems > 0 ? '#1565c0' : '#9e9e9e'}
                      x={24}
                      y={1}
                      width={22}
                      height={shelfH - 2}
                      verticalAlign="middle"
                    />
                    {productLabel && safeWidth > 80 && (
                      <Text
                        text={productLabel}
                        fontSize={Math.max(5, labelFontSize - 1)}
                        fontFamily="Roboto, sans-serif"
                        fill="#546e7a"
                        x={46}
                        y={1}
                        width={safeWidth - 54}
                        height={shelfH - 2}
                        verticalAlign="middle"
                        ellipsis
                      />
                    )}
                  </Group>
                );
              })}
            </Group>
          );
        })() : (
          element.stockCount !== undefined && isFinite(element.stockCount) && (
            <Text
              text={`📦 ${element.stockCount}`}
              fontSize={10}
              fontFamily="Roboto, sans-serif"
              fill="#666"
              x={4}
              y={Math.max(10, safeHeight - 16)}
              listening={false}
            />
          )
        )}
      </Group>
    );
  };
  
  // Toolbar controls
  const handleZoomIn = () => setZoom(prev => Math.min(5, prev * 1.2));
  const handleZoomOut = () => setZoom(prev => Math.max(0.1, prev / 1.2));
  const handleCenter = () => {
    setZoom(1);
    setStagePos({ x: 0, y: 0 });
  };
  
  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor: '#fafafa',
        borderRadius: 2,
        cursor: readOnly ? 'grab' : mode === 'set_start_point' ? 'crosshair' : mode === 'pan' ? 'grab' : 'default',
        overflow: 'hidden',
      }}
    >
      {/* Vertical Toolbar */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '2px',
        }}
      >
        {/* Mode tools (hidden in readOnly/mobile) */}
        {!readOnly && (
        <Paper
          elevation={1}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '8px',
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          <Tooltip title={t('warehouseMap.tools.select')} placement="right">
            <IconButton
              size="small"
              onClick={() => onModeChange('select')}
              sx={{
                borderRadius: 0,
                width: 36,
                height: 36,
                bgcolor: mode === 'select' ? 'primary.main' : 'transparent',
                color: mode === 'select' ? 'primary.contrastText' : 'text.secondary',
                '&:hover': { bgcolor: mode === 'select' ? 'primary.dark' : 'action.hover' },
              }}
            >
              <SelectIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('warehouseMap.tools.pan')} placement="right">
            <IconButton
              size="small"
              onClick={() => onModeChange('pan')}
              sx={{
                borderRadius: 0,
                width: 36,
                height: 36,
                bgcolor: mode === 'pan' ? 'primary.main' : 'transparent',
                color: mode === 'pan' ? 'primary.contrastText' : 'text.secondary',
                '&:hover': { bgcolor: mode === 'pan' ? 'primary.dark' : 'action.hover' },
              }}
            >
              <PanIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Paper>
        )}

        {/* Zoom tools */}
        <Paper
          elevation={1}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '8px',
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          <Tooltip title={t('warehouseMap.tools.zoomIn')} placement="right">
            <IconButton
              size="small"
              onClick={handleZoomIn}
              sx={{ borderRadius: 0, width: 36, height: 36, color: 'text.secondary' }}
            >
              <ZoomInIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('warehouseMap.tools.zoomOut')} placement="right">
            <IconButton
              size="small"
              onClick={handleZoomOut}
              sx={{ borderRadius: 0, width: 36, height: 36, color: 'text.secondary' }}
            >
              <ZoomOutIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('warehouseMap.tools.center')} placement="right">
            <IconButton
              size="small"
              onClick={handleCenter}
              sx={{ borderRadius: 0, width: 36, height: 36, color: 'text.secondary' }}
            >
              <CenterIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{
              fontSize: 10,
              lineHeight: 1,
              py: 0.5,
              color: 'text.disabled',
              userSelect: 'none',
            }}
          >
            {(zoom * 100).toFixed(0)}%
          </Typography>
        </Paper>

        {/* View tools (hidden in readOnly/mobile) */}
        {!readOnly && (
        <Paper
          elevation={1}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '8px',
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          <Tooltip title={showGrid ? t('warehouseMap.tools.hideGrid') : t('warehouseMap.tools.showGrid')} placement="right">
            <IconButton
              size="small"
              onClick={onToggleGrid}
              sx={{
                borderRadius: 0,
                width: 36,
                height: 36,
                color: showGrid ? 'primary.main' : 'text.secondary',
              }}
            >
              {showGrid ? <GridOffIcon sx={{ fontSize: 18 }} /> : <GridIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>

          {onStartPointSet && (
            <Tooltip
              title={mode === 'set_start_point' ? 'Kliknij na mapie aby ustawić punkt startowy' : 'Ustaw punkt startowy'}
              placement="right"
            >
              <IconButton
                size="small"
                onClick={() => onModeChange(mode === 'set_start_point' ? 'select' : 'set_start_point' as any)}
                sx={{
                  borderRadius: 0,
                  width: 36,
                  height: 36,
                  bgcolor: mode === 'set_start_point' ? 'success.main' : 'transparent',
                  color: mode === 'set_start_point' ? 'success.contrastText' : 'text.secondary',
                  '&:hover': { bgcolor: mode === 'set_start_point' ? 'success.dark' : 'action.hover' },
                  animation: mode === 'set_start_point' ? 'pulse-sp 1.5s infinite' : 'none',
                  '@keyframes pulse-sp': {
                    '0%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.4)' },
                    '70%': { boxShadow: '0 0 0 6px rgba(76, 175, 80, 0)' },
                    '100%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)' },
                  },
                }}
              >
                <HomeIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}

          {onToggleNavigation && (
            <Tooltip title={showNavigation ? 'Ukryj graf nawigacji' : 'Pokaż graf nawigacji'} placement="right">
              <IconButton
                size="small"
                onClick={onToggleNavigation}
                sx={{
                  borderRadius: 0,
                  width: 36,
                  height: 36,
                  color: showNavigation ? 'primary.main' : 'text.secondary',
                }}
              >
                <NavigationIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}

          {onTogglePickingPath && pickingPath && (
            <Tooltip title={showPickingPath ? 'Ukryj ścieżkę' : 'Pokaż ścieżkę'} placement="right">
              <IconButton
                size="small"
                onClick={onTogglePickingPath}
                sx={{
                  borderRadius: 0,
                  width: 36,
                  height: 36,
                  color: showPickingPath ? 'warning.main' : 'text.secondary',
                }}
              >
                <RouteIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Paper>
        )}
      </Box>
      
      {/* Set start point mode banner */}
      {mode === 'set_start_point' && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: 48,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            px: 3,
            py: 1,
            borderRadius: 2,
            bgcolor: '#4caf50',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <HomeIcon fontSize="small" />
          <Typography variant="body2" fontWeight="bold">
            Kliknij na mapie, aby ustawić punkt startowy (biuro)
          </Typography>
        </Paper>
      )}
      
      {/* Canvas - render only when dimensions are stable to avoid transient 0x0 internal canvases */}
      {canRenderStage && (
      <>
      {/* react-konva Stage accepts children; some TS/build envs omit children on StageProps */}
      {/* @ts-ignore */}
      <Stage
        ref={stageRef}
        width={Math.max(1, containerSize.width)}
        height={Math.max(1, containerSize.height)}
        scaleX={zoom}
        scaleY={zoom}
        x={stagePos.x}
        y={stagePos.y}
        draggable={mode === 'pan' || readOnly}
        onWheel={handleWheel}
        onClick={handleStageClick}
        onDragEnd={(e: KonvaEventObject<DragEvent>) => {
          if (mode === 'pan' || readOnly) {
            setStagePos({ x: e.target.x(), y: e.target.y() });
          }
        }}
        onTouchMove={(e: KonvaEventObject<TouchEvent>) => {
          const touch = e.evt.touches;
          if (touch.length === 2) {
            e.evt.preventDefault();
            const dist = Math.hypot(
              touch[0].clientX - touch[1].clientX,
              touch[0].clientY - touch[1].clientY,
            );
            const stage = stageRef.current;
            if (!stage) return;
            if (!(stage as any).__lastPinchDist) {
              (stage as any).__lastPinchDist = dist;
              return;
            }
            const oldScale = zoom;
            const newScale = Math.min(5, Math.max(0.1, oldScale * (dist / (stage as any).__lastPinchDist)));
            (stage as any).__lastPinchDist = dist;

            const center = {
              x: (touch[0].clientX + touch[1].clientX) / 2,
              y: (touch[0].clientY + touch[1].clientY) / 2,
            };
            const rect = stage.container().getBoundingClientRect();
            const pointer = { x: center.x - rect.left, y: center.y - rect.top };
            const mousePointTo = {
              x: (pointer.x - stagePos.x) / oldScale,
              y: (pointer.y - stagePos.y) / oldScale,
            };
            setZoom(newScale);
            setStagePos({
              x: pointer.x - mousePointTo.x * newScale,
              y: pointer.y - mousePointTo.y * newScale,
            });
          }
        }}
        onTouchEnd={() => {
          const stage = stageRef.current;
          if (stage) (stage as any).__lastPinchDist = 0;
        }}
      >
        {/* Background layer */}
        <Layer>
          {/* Map background */}
          <Rect
            x={0}
            y={0}
            width={safeMapWidth}
            height={safeMapHeight}
            fill="#ffffff"
            stroke="#e0e0e0"
            strokeWidth={2}
          />
          
          {/* Background image */}
          {bgImage && bgImage.width > 0 && bgImage.height > 0 && (
            <KonvaImage
              image={bgImage}
              x={0}
              y={0}
              width={safeMapWidth}
              height={safeMapHeight}
              opacity={0.3}
            />
          )}
          
          {/* Grid */}
          {renderGrid()}
        </Layer>
        
        {/* Elements layer */}
        <Layer>
          {Array.isArray(elements) && elements
            .filter(el => el !== null && el !== undefined)
            .map(renderElement)
            .filter(el => el !== null)}
          
          {/* Transformer for selected element */}
          <Transformer
            ref={transformerRef}
            boundBoxFunc={(oldBox: TransformerBoundBox, newBox: TransformerBoundBox) => {
              // Limit minimum size
              if (newBox.width < 20 || newBox.height < 20) {
                return oldBox;
              }
              return newBox;
            }}
            rotateEnabled={true}
            enabledAnchors={[
              'top-left',
              'top-right',
              'bottom-left',
              'bottom-right',
              'middle-right',
              'middle-left',
              'top-center',
              'bottom-center',
            ]}
          />
        </Layer>
        
        {/* Navigation graph layer */}
        <Layer listening={false}>
          {renderNavigationLayer()}
        </Layer>
        
        {/* Picking path layer */}
        <Layer listening={false}>
          {renderPickingPathLayer()}
        </Layer>
        
        {/* Start point marker layer */}
        {startPoint && startPoint.x != null && startPoint.y != null && (
          <Layer listening={false}>
            {/* Outer ring */}
            <Circle
              x={startPoint.x}
              y={startPoint.y}
              radius={18}
              fill="rgba(76, 175, 80, 0.2)"
              stroke="#4caf50"
              strokeWidth={2}
            />
            {/* Inner dot */}
            <Circle
              x={startPoint.x}
              y={startPoint.y}
              radius={8}
              fill="#4caf50"
              stroke="#fff"
              strokeWidth={2}
              shadowColor="black"
              shadowBlur={6}
              shadowOpacity={0.4}
            />
            {/* Home icon - simple house shape */}
            <Text
              x={startPoint.x - 5}
              y={startPoint.y - 5}
              text="⌂"
              fontSize={12}
              fill="#fff"
              fontStyle="bold"
            />
            {/* Label */}
            <Rect
              x={startPoint.x - 35}
              y={startPoint.y - 32}
              width={70}
              height={16}
              fill="rgba(76, 175, 80, 0.85)"
              cornerRadius={3}
            />
            <Text
              x={startPoint.x - 35}
              y={startPoint.y - 31}
              text={startPoint.name || 'START'}
              fontSize={10}
              fill="#fff"
              fontStyle="bold"
              width={70}
              align="center"
            />
          </Layer>
        )}
      </Stage>
      </>
      )}

      {/* Unfulfilled items warning overlay */}
      {showPickingPath && pickingPath && !pickingPath.isComplete && pickingPath.unfulfilledItems.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            bottom: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: '#fff3e0',
            border: '2px solid #ff9800',
            borderRadius: 2,
            px: 2,
            py: 1,
            maxWidth: 420,
            zIndex: 10,
          }}
        >
          <Typography variant="subtitle2" sx={{ color: '#e65100', fontWeight: 700, mb: 0.5 }}>
            ⚠ Brak pełnej ilości produktów
          </Typography>
          {pickingPath.unfulfilledItems.map((item) => (
            <Typography key={item.productId} variant="body2" sx={{ color: '#bf360c' }}>
              • {item.productName}: brakuje {item.quantityMissing} szt. (potrzeba {item.quantityNeeded})
            </Typography>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default MapCanvas;

